/**
 * Cross-tab session signals (BroadcastChannel-based, same-origin only).
 *
 * Design constraints (deliberate):
 *   - Receivers MUST NOT take destructive actions in response to a message.
 *     A spurious broadcast (XSS, hostile extension) should at worst show a
 *     UI nudge — never wipe IndexedDB, never clear masterKey, never
 *     terminate WebSocket connections.
 *   - Messages are best-effort UX hints, not authoritative commands.
 *   - Same-origin only: BroadcastChannel cannot be reached from the network.
 *     We rely on that boundary plus non-destructive receiver semantics
 *     instead of trying to sign messages (the signing key would have to
 *     live in the same origin and be readable by any in-page attacker).
 *
 * Today the only signal is `logout` — emitted after a logout succeeds in
 * one tab so other tabs can show a "signed out elsewhere" banner. The
 * existing on-401 path in those tabs already handles state cleanup
 * locally if/when the user takes action.
 */

const CHANNEL_NAME = "orbital-session";

let channel = null;
const handlers = new Set();

function ensureChannel() {
  if (channel) return channel;
  if (typeof BroadcastChannel === "undefined") return null;
  try {
    channel = new BroadcastChannel(CHANNEL_NAME);
    channel.addEventListener("message", (event) => {
      const msg = event?.data;
      if (!isValidMessage(msg)) return;
      for (const fn of handlers) {
        try {
          fn(msg);
        } catch (err) {
          console.error("[CrossTabSession] handler threw:", err);
        }
      }
    });
  } catch (err) {
    console.warn("[CrossTabSession] BroadcastChannel unavailable:", err);
    channel = null;
  }
  return channel;
}

function isValidMessage(msg) {
  if (!msg || typeof msg !== "object") return false;
  if (typeof msg.type !== "string") return false;
  // Whitelist of recognized types — anything else is silently ignored.
  return msg.type === "logout";
}

/**
 * Subscribe to incoming cross-tab session messages.
 * @param {(msg: {type: string, at?: number}) => void} handler
 * @returns {() => void} unsubscribe
 */
export function onSessionMessage(handler) {
  ensureChannel();
  handlers.add(handler);
  return () => handlers.delete(handler);
}

/**
 * Broadcast that this tab has logged out. Receivers should show a UI
 * nudge only — no destructive actions.
 */
export function broadcastLogout() {
  const ch = ensureChannel();
  if (!ch) return;
  try {
    ch.postMessage({ type: "logout", at: Date.now() });
  } catch (err) {
    console.warn("[CrossTabSession] broadcastLogout failed:", err);
  }
}

/**
 * Tear down the channel — call on full app shutdown if needed. Not
 * strictly necessary; browsers GC the channel when the tab closes.
 */
export function destroyCrossTabSession() {
  if (channel) {
    try {
      channel.close();
    } catch {}
    channel = null;
  }
  handlers.clear();
}
